import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";
import type { EntitySoccerRoles, InvitationModel, PaginationTokenType } from "shared/types";
import { BackendEntityType, EntitySoccerClubRoles } from "shared/types";
import { getFollowParamName, getInviteFollowEPURL } from "utils/getFollowEPURL";

import BaseConverter from "./converters/baseConverter";

const useInvitationApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			getInfo: async (invitationId: string) => {
				return BaseConverter.convert<{ invitation: InvitationModel }>(
					await actions.get(API_URLS.INVITATION.GET, {
						"invitation-id": invitationId
					})
				);
			},
			inviteReferees: async (refereeOrganizationId: string, emails: string[]) => {
				return BaseConverter.convert<{ invitations: InvitationModel[] }>(
					await actions.post(API_URLS.INVITATION.INVITE_REFEREES, {
						refereeOrganizationId,
						referees: emails.map(email => ({
							email,
							firstName: null,
							lastName: null,
							gender: null,
							birthDate: null
						}))
					})
				);
			},
			acceptInvitation: async (params: {
				invitationId: string;
				asPersonId?: string;
				additionalInfo?: {
					jerseyNumber?: number;
					guardianId?: string;
					waivers?: {
						accepted?: { id: string }[];
						rejected?: { id: string }[];
					};
				};
			}) => {
				return BaseConverter.convert<InvitationModel>(
					await actions.post(API_URLS.INVITATION.ACCEPT_INVITATION, params)
				);
			},
			getInvitations: async (limit: number, nextToken?: string) => {
				return BaseConverter.convert<{
					invitations: InvitationModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(await actions.get(API_URLS.INVITATION.LIST_INVITATIONS, { limit, before: nextToken }));
			},
			inviteToFollowEntity: async ({
				emails,
				entityId,
				entityType
			}: {
				entityId: string;
				emails: string[];
				entityType: BackendEntityType | EntitySoccerRoles;
			}) => {
				const url = getInviteFollowEPURL(entityType);

				if (url) {
					return BaseConverter.convert<{ createdInvitationCount: number }>(
						await actions.post(url, {
							[entityType === BackendEntityType.CorePerson || entityType === EntitySoccerClubRoles.PLAYER
								? "personId"
								: getFollowParamName(entityType)]: entityId,
							emails
						})
					);
				}
			}
		}),
		[actions]
	);
};

export default useInvitationApiService;
